<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { cx } from './cx'
import Spinner from './Spinner.vue'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-accent text-[var(--color-rail)] border-accent hover:bg-[#1ad4bb] active:scale-[0.98] active:bg-accent-dim',
  secondary: 'bg-panel text-text border-line-strong hover:bg-hover active:scale-[0.98]',
  ghost: 'bg-transparent text-muted border-transparent hover:bg-hover hover:text-text active:scale-[0.98]',
  danger: 'bg-transparent text-danger border-danger/35 hover:bg-danger/8 active:scale-[0.98]',
}

const SIZES: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[12px] gap-1.5',
  md: 'h-9 px-3.5 text-[13px] gap-2',
}

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    loading?: boolean
    disabled?: boolean
    type?: 'button' | 'submit' | 'reset'
    class?: string
  }>(),
  {
    variant: 'secondary',
    size: 'md',
    loading: false,
    disabled: false,
    type: 'button',
  },
)

const attrs = useAttrs()

const classes = computed(() =>
  cx(
    'inline-flex items-center justify-center rounded-md border font-semibold whitespace-nowrap',
    'transition-[background-color,border-color,color,transform] duration-150 ease-out cursor-pointer select-none',
    'disabled:opacity-45 disabled:cursor-not-allowed disabled:active:scale-100',
    VARIANTS[props.variant],
    SIZES[props.size],
    props.class,
  ),
)
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="classes"
    v-bind="attrs"
  >
    <Spinner v-if="loading" :size="13" />
    <slot />
  </button>
</template>
